import {z} from 'zod';
import {extensionKeySchema} from '../shared';

export type GSAPValue = string | number | boolean;

/**
 * Animation value schema
 * Flexible schema to support various plugin data shapes:
 * - Single values: "1" | 100 | true | {...object}
 * - From values: ["0"] or [{...object}]
 * - FromTo values: ["0", "1"] or [null, "1"] or [{...}, {...}]
 *
 * This schema is intentionally permissive to support arbitrary plugin value shapes
 * as acknowledged tech debt at the core type level
 */
export const animationValueSchema = z.union([
  // Single value (to) - any type including objects
  z.unknown(),
  // From value [from] - array with single element
  z.array(z.unknown()).length(1),
  // FromTo value [from, to] - array with two elements
  z.array(z.unknown()).length(2),
]) as z.ZodType<
  GSAPValue | [GSAPValue] | [GSAPValue | null, GSAPValue] | unknown
>;
export type AnimationValue<_TSpecial = unknown> = z.infer<
  typeof animationValueSchema
>;

export const animationConfigValuesSchema = z.record(
  z.string(), // PropertyName
  z.union([z.null(), animationValueSchema])
);
export type PropertyName = string;
export type AnimationConfigValues = z.infer<typeof animationConfigValuesSchema>;

export const animationPropertiesSchema = z.record(
  extensionKeySchema,
  animationConfigValuesSchema
);
export type AnimationProperties = z.infer<typeof animationPropertiesSchema>;

// ============================================================================
// Random & additive value wrappers
// ============================================================================
//
// Both ride the intentionally-permissive `animationValueSchema` (the `z.unknown()`
// arm), so persisted interactions are unaffected and no field is added to the
// action/timeline schemas. The `type: 'ix3-*'` discriminator mirrors
// `StructuredTransformOrigin` (`type: 'ix3-origin'` in
// `@packages/systems/ix3/shared`) so the variable-resolution pass treats each
// wrapper as an opaque leaf. The runtime action handlers convert these to GSAP
// strings inside `createTweenConfig`.

/**
 * Random value wrapper. Stored in a property's `to`/`from` value slot in place
 * of a scalar; the runtime emits GSAP `random(min, max[, step])` from it.
 */
export const randomValueSchema = z.object({
  type: z.literal('ix3-random'),
  /** Inclusive lower bound. */
  min: z.number(),
  /** Inclusive upper bound. */
  max: z.number(),
  /** Optional snap increment → GSAP `random(min, max, step)`. */
  step: z.number().optional(),
  /** CSS unit appended to the emitted value (e.g. "px", "deg"). */
  unit: z.string().optional(),
});
export type RandomValue = z.infer<typeof randomValueSchema>;

/**
 * Additive value wrapper. Marks a property value as relative; the runtime
 * emits GSAP `+=` so re-firing a trigger accumulates instead of replaying to a
 * fixed endpoint. `value` carries its own sign and may itself be a
 * {@link RandomValue} to compose `+=random(min, max)`.
 */
export const additiveValueSchema = z.object({
  type: z.literal('ix3-additive'),
  value: z.union([z.number(), randomValueSchema]),
  /** CSS unit appended to the emitted value (e.g. "px", "deg"). */
  unit: z.string().optional(),
});
export type AdditiveValue = z.infer<typeof additiveValueSchema>;

/**
 * Random-from-set value wrapper. Unlike {@link RandomValue} (a numeric min/max
 * range), this picks a random entry from a fixed set each time the animation
 * plays — the runtime emits GSAP `random([a, b, c])`. Numeric properties can
 * opt into this as an alternative to a min/max range; color properties use it
 * exclusively (a random pick from a palette, avoiding the need to interpolate a
 * random color between two endpoints).
 *
 * `values` are numbers for numeric properties (sharing a single `unit`) and
 * comma-free strings (hex colors) for color properties — comma-free because
 * GSAP splits the `random([...])` list on commas.
 */
export const randomArrayValueSchema = z.object({
  type: z.literal('ix3-random-array'),
  /** Candidate values to pick from; one is chosen at random on each play. */
  values: z.array(z.union([z.number(), z.string()])),
  /** CSS unit appended to the emitted value (numeric properties only). */
  unit: z.string().optional(),
});
export type RandomArrayValue = z.infer<typeof randomArrayValueSchema>;

// These guards narrow arbitrary persisted data: `animationValueSchema` accepts
// `unknown`, so a malformed `{type: 'ix3-random'}` can pass the timeline schema.
// They verify every required field (not just the discriminator) so callers that
// read `min`/`max`/`value` after the guard can trust those fields are present.
// Kept structural (rather than `randomValueSchema.safeParse`) so the runtime and
// designer copies stay byte-for-byte identical.

/** True when `value` is a {@link RandomValue} wrapper. */
export function isRandomValue(value: unknown): value is RandomValue {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    v.type === 'ix3-random' &&
    typeof v.min === 'number' &&
    typeof v.max === 'number' &&
    (v.step === undefined || typeof v.step === 'number') &&
    (v.unit === undefined || typeof v.unit === 'string')
  );
}

/** True when `value` is an {@link AdditiveValue} wrapper. */
export function isAdditiveValue(value: unknown): value is AdditiveValue {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    v.type === 'ix3-additive' &&
    (typeof v.value === 'number' || isRandomValue(v.value)) &&
    (v.unit === undefined || typeof v.unit === 'string')
  );
}

/** True when `value` is a {@link RandomArrayValue} wrapper. */
export function isRandomArrayValue(value: unknown): value is RandomArrayValue {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    v.type === 'ix3-random-array' &&
    Array.isArray(v.values) &&
    v.values.every(
      (entry) => typeof entry === 'number' || typeof entry === 'string'
    ) &&
    (v.unit === undefined || typeof v.unit === 'string')
  );
}
